/**
 * Drawer — slide right padronizado pra ProductDetailsPage.
 *
 * Uso:
 *   <Drawer
 *     isOpen={open}
 *     onClose={close}
 *     title="Adicionar item"
 *     subtitle="Cadastre um novo produto físico ou digital"
 *     icon="mdi:package-variant"
 *     size="md"          // 'md' (default) | 'lg'
 *     onSubmit={save}    // se passado, body vira <form> e o footer Salvar dispara submit
 *     submitting={loading}
 *     submitLabel="Salvar"
 *     cancelLabel="Cancelar"
 *   >
 *     ...body
 *   </Drawer>
 */

import React, { useEffect } from 'react';
import { Icon } from '@iconify/react';
import './Drawer.css';

export const Drawer = ({
  isOpen,
  onClose,
  title,
  subtitle,
  icon = 'mdi:pencil-outline',
  size = 'md',
  onSubmit,
  submitting = false,
  submitLabel = 'Salvar',
  cancelLabel = 'Cancelar',
  footer,
  children,
}) => {
  // ESC fecha
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // Body scroll lock
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit?.(e);
  };

  const BodyWrapper = onSubmit ? 'form' : 'div';

  return (
    <>
      <div
        className={`drawer-backdrop ${isOpen ? 'is-open' : ''}`}
        onClick={onClose}
      />
      <aside
        className={`drawer ${size === 'lg' ? 'drawer--lg' : ''} ${isOpen ? 'is-open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header className="drawer__header">
          <div className="drawer__title-wrap">
            <div className="drawer__icon">
              <Icon icon={icon} width="20" />
            </div>
            <div>
              <span className="drawer__title">{title}</span>
              {subtitle && <span className="drawer__subtitle">{subtitle}</span>}
            </div>
          </div>
          <button
            type="button"
            className="drawer__close"
            onClick={onClose}
            aria-label="Fechar"
          >
            <Icon icon="mdi:close" width="18" />
          </button>
        </header>

        <BodyWrapper
          className="drawer__body"
          {...(onSubmit ? { onSubmit: handleSubmit } : {})}
        >
          {children}
        </BodyWrapper>

        <footer className="drawer__footer">
          {footer || (
            <>
              <button
                type="button"
                className="drawer-btn"
                onClick={onClose}
                disabled={submitting}
              >
                {cancelLabel}
              </button>
              {onSubmit && (
                <button
                  type="button"
                  className="drawer-btn drawer-btn--primary"
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Icon icon="mdi:loading" className="spin" width="14" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Icon icon="mdi:check" width="14" />
                      {submitLabel}
                    </>
                  )}
                </button>
              )}
            </>
          )}
        </footer>
      </aside>
    </>
  );
};

export default Drawer;
